import React, { useEffect, useState } from "react";
import { useAppContext } from "../context/AppContext";
import {
  getAllLanguages,
  getBrandDetails,
  getDivisionsWithBrand,
  getModels,
  setDefaultValues,
} from "../services/app.service";
import { PERSONA_INITIAL_OBJECT } from "../utils/settings";
import { useNavigate } from "react-router-dom";
import icon_loading from "../../../assets/elysia-loader.gif";
import IconTick from "../../../assets/icon-google-tick.svg";
import IconAngleDown from "../../../assets/icon-arrow-down.svg";

const Settings = () => {
  const [tempSelectedModel, setTempSelectedModel] = useState({
    name_of_model: "",
    display_name: "",
    model: "",
  });
  const [tempSelectedLanguage, setTempSelectedLanguage] = useState("");
  const [tempSelectedDivision, setTempSelectedDivision] = useState("");
  const [tempSelectedBrand, setTempSelectedBrand] = useState<Brand>({ id: "", name: "" });
  const [tempSelectedPersona, setTempSelectedPersona] = useState<Persona>(PERSONA_INITIAL_OBJECT);
  const [allPersonas, setAllPersonas] = useState<any>([]);

  const [allModels, setAllModels] = useState<Model[]>([]);
  const [allBrands, setAllBrands] = useState<Brand[]>([]);
  const [allLanguages, setAllLanguages] = useState<string[]>([]);
  const [allDivisions, setAllDivisions] = useState([]);
  const [tempSelectedTovId, setTempSelectedTovId] = useState("");
  const [loadingOnSave, setLodingOnSave] = useState(false);
  const [showSavedBadge, setShowSavedBadge] = useState(false);
  const [canSave, setCanSave] = useState(true);
  const [showError, setShowError] = useState(false);

  const {
    selectedModel,
    selectedLanguage,
    selectedDivision,
    selectedBrand,
    selectedPersona,
    setSelectedModel,
    setSelectedLanguage,
    setSelectedDivision,
    setSelectedBrand,
    setSelectedPersona,
    setSelectedTovId,
    setModelProvider,
  } = useAppContext();

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    const allModels = async () => {
      setIsLoading(true);
      const response = await getModels();
      setAllModels(response ?? []);
      setIsLoading(false);
    };
    const allLanguages = async () => {
      const response = await getAllLanguages();
      setAllLanguages(response);
    };
    allModels();
    allLanguages();
  }, []);

  useEffect(() => {
    const getDivisionAndBrands = async () => {
      const response = await getDivisionsWithBrand();
      setAllDivisions?.(response);
    };
    getDivisionAndBrands();
  }, []);

  useEffect(() => {
    setTempSelectedLanguage(selectedLanguage);
    setTempSelectedDivision(selectedDivision);
    setTempSelectedBrand(selectedBrand);
    setTempSelectedPersona(selectedPersona);
  }, []);

  useEffect(() => {
    if (!selectedModel || !allModels?.length) return;

    const defaultModel = allModels.find((model) => model.name_of_model === selectedModel);

    if (defaultModel) {
      setTempSelectedModel({
        name_of_model: defaultModel.name_of_model,
        display_name: defaultModel.display_name,
        model: defaultModel.model,
      });
    }
  }, [selectedModel, allModels]);

  useEffect(() => {
    if (!allDivisions?.length) return;
    setAllBrands(allDivisions?.find((div) => div.division === tempSelectedDivision)?.brand);
  }, [tempSelectedDivision, allDivisions]);

  useEffect(() => {
    const fetchPersonas = async () => {
      const res = await getBrandDetails(tempSelectedBrand.id);
      if (res) {
        setAllPersonas(res?.communication?.target_audiences);
        setTempSelectedTovId(res?.id);
        setCanSave(true);
        setShowError(false);
      } else {
        setShowError(true);
        setCanSave(false);
      }
    };
    if (tempSelectedBrand?.id?.length) {
      fetchPersonas();
    }
  }, [tempSelectedBrand]);

  const handleModelSelect = (e) => {
    const modelDetails = allModels?.find((model) => model.display_name === e.target.value);
    setTempSelectedModel(modelDetails);
    setTempSelectedModel({
      name_of_model: modelDetails?.name_of_model,
      display_name: modelDetails?.display_name,
      model: modelDetails?.model,
    });
  };

  const handleLangaugeSelect = (e) => {
    const value = e.target.value;
    setTempSelectedLanguage(value);
  };

  const handleDivisionSelect = (e) => {
    const value = e.target.value;
    setTempSelectedDivision(value);
    setTempSelectedBrand({ id: "", name: "" });
    setTempSelectedPersona(PERSONA_INITIAL_OBJECT);
    setAllPersonas([]);
    setCanSave(false);
    setShowError(false);
  };

  const handleBrandSelect = (e) => {
    setAllPersonas([]);
    setTempSelectedBrand(
      allBrands?.find((brand) => brand.id === e.target.value) ?? { id: "", name: "" }
    );
    setTempSelectedPersona(PERSONA_INITIAL_OBJECT);
  };

  const handlePersonaSelect = (e) => {
    setTempSelectedPersona(
      allPersonas?.find((persona) => persona.id === e.target.value) ?? PERSONA_INITIAL_OBJECT
    );
  };

  const handleSave = async () => {
    try {
      setLodingOnSave(true);
      await setDefaultValues(
        tempSelectedModel.name_of_model,
        tempSelectedLanguage,
        tempSelectedTovId,
        tempSelectedPersona.id
      );
      setSelectedModel(tempSelectedModel.name_of_model);
      setSelectedLanguage(tempSelectedLanguage);
      setSelectedDivision(tempSelectedDivision);
      setSelectedBrand(tempSelectedBrand);
      setSelectedTovId(tempSelectedTovId);
      setSelectedPersona(tempSelectedPersona);
      setModelProvider(tempSelectedModel.model);
      setLodingOnSave(false);
      setShowSavedBadge(true);
      setTimeout(() => {
        setShowSavedBadge(false);
      }, 1000);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="settings-wrapper">
      <div className={`settings-container ${isLoading ? "loading" : ""}`}>
        <div className="settings-container">
          <div className="settings-sub-headings-container">
            <div className="settings-sub-headings">
              <div className="settings-heading">LLM Model</div>

              <label className="select-label" htmlFor="llm-model">
                Select LLM Model*
              </label>

              <div className="select-wrap">
                <select
                  id="llm-model"
                  className="settings-dropdowns"
                  value={tempSelectedModel.display_name}
                  onChange={handleModelSelect}
                  aria-label="Select LLM Model"
                >
                  {allModels?.map((model, idx) => (
                    <option key={idx} value={model.display_name}>
                      {model.display_name}
                    </option>
                  ))}
                </select>
                <IconAngleDown />
              </div>
            </div>
            <hr className="settings-divider" />
            <div className="settings-sub-headings">
              <div className="settings-heading">Language</div>

              <label className="select-label" htmlFor="Select Language">
                Select Language*
              </label>

              <div className="select-wrap">
                <select
                  id="Select Language"
                  className="settings-dropdowns"
                  value={tempSelectedLanguage}
                  onChange={handleLangaugeSelect}
                  aria-label="Select Language"
                >
                  {allLanguages.map((language, idx) => (
                    <option key={idx} value={language}>
                      {language}
                    </option>
                  ))}
                </select>
                <IconAngleDown />
              </div>
            </div>
            <hr className="settings-divider" />
            <div className="settings-sub-headings">
              <div className="settings-heading">Tone Of Voice</div>

              <label className="select-label" htmlFor="Division">
                Division*
              </label>

              <div className="select-wrap">
                <select
                  id="Division"
                  className="settings-dropdowns"
                  value={tempSelectedDivision}
                  onChange={handleDivisionSelect}
                  aria-label="Division"
                >
                  {allDivisions?.map((division, idx) => (
                    <option key={idx} value={division.division}>
                      {division.division}
                    </option>
                  ))}
                </select>
                <IconAngleDown />
              </div>
              <label className="select-label" htmlFor="Brand">
                Brand*
              </label>
              {showError && (
                <div className="error-msg">
                  Sorry no tone of voice created for the selected brand
                </div>
              )}

              <div className="select-wrap">
                <select
                  id="Brand"
                  className="settings-dropdowns"
                  value={tempSelectedBrand.id}
                  onChange={handleBrandSelect}
                  aria-label="Brand"
                >
                  <option value="">Please select a brand</option>
                  {allDivisions
                    ?.find((div) => div.division === tempSelectedDivision)
                    ?.brand.map((brand) => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                </select>
                <IconAngleDown />
              </div>
              <label className="select-label" htmlFor="Persona">
                Persona
              </label>

              <div className="select-wrap">
                <select
                  id="Persona"
                  className="settings-dropdowns"
                  value={tempSelectedPersona?.id}
                  onChange={handlePersonaSelect}
                  aria-label="Persona"
                  disabled={!allPersonas?.length}
                >
                  <option value="">Please select an optional persona</option>
                  {allPersonas?.length &&
                    allPersonas?.map((persona) => (
                      <option key={persona?.id} value={persona?.id}>
                        {persona?.name}
                      </option>
                    ))}
                </select>
                <IconAngleDown />
              </div>
            </div>
          </div>
          {showSavedBadge && (
            <span className="badge-for-save-button">
              <IconTick />
              <span>Saved</span>
            </span>
          )}
          <div className="settings-buttons">
            <button className="cancel-btn" onClick={() => navigate("/")}>
              Cancel
            </button>
            <button
              className="save-btn"
              disabled={!canSave || tempSelectedBrand.id === ""}
              onClick={handleSave}
            >
              <div className="btn-content">
                {loadingOnSave ? <img src={icon_loading} alt="loading" /> : "Save"}
              </div>
            </button>
          </div>
        </div>
      </div>
      {isLoading && (
        <div className="settings-loading-overlay">
          <img src={icon_loading} alt="saving..." />
        </div>
      )}
    </div>
  );
};

export default Settings;
